import React, { useMemo, useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { GenericSelect } from '@/styles/GenericForm';
import { cn } from '@/lib/utils';

export interface CheckboxListOption {
  value: string;
  label: string;
  /** A short second line (e.g. what a vendor is good for). */
  description?: string;
  /** What each filter looks at: `{ cost_code: [<id>, ...] }`. */
  attributes?: Record<string, string[]>;
}

export interface CheckboxListFilter {
  key: string;
  label: string;
  options: { label: string; value: string }[];
}

interface GenericCheckboxListProps {
  id?: string;
  options: CheckboxListOption[];
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  /** Narrow the list by an attribute of the options (a cost code, a status...). A selected option stays selected when a filter hides it. */
  filters?: CheckboxListFilter[];
  searchPlaceholder?: string;
  emptyText?: string;
  maxHeightClassName?: string;
}

/**
 * THE multiple-choice picker of the app: a searchable list of options with a checkbox each, optional filters and a "N selected" counter. Used inside GenericForm
 * (field type `multiselect`) and anywhere else a set of things must be chosen. It only edits the list of selected values; what they mean is up to the caller.
 */
export function GenericCheckboxList({
  id, options, value, onChange, disabled = false, filters = [], searchPlaceholder = 'Search', emptyText = 'Nothing to select', maxHeightClassName = 'max-h-48',
}: GenericCheckboxListProps) {
  const [search, setSearch] = useState('');
  const [active, setActive] = useState<Record<string, string>>({});
  const selected = useMemo(() => new Set(value), [value]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return options.filter((option) => {
      if (term && !`${option.label} ${option.description || ''}`.toLowerCase().includes(term)) return false;
      return Object.entries(active).every(([key, wanted]) => !wanted || (option.attributes?.[key] || []).includes(wanted));
    });
  }, [options, search, active]);

  const toggle = (optionValue: string, checked: boolean) => {
    const next = new Set(selected);
    if (checked) next.add(optionValue); else next.delete(optionValue);
    onChange(Array.from(next));
  };

  return (
    <div className="space-y-2" id={id}>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          disabled={disabled}
          className="font-form-input generic-form-field flex-1 min-w-[10rem]"
        />
        {filters.map((filter) => (
          <div key={filter.key} className="w-44">
            <GenericSelect
              value={active[filter.key] || ''}
              onValueChange={(next) => setActive((prev) => ({ ...prev, [filter.key]: next }))}
              placeholder={filter.label}
              searchable
              disabled={disabled}
              options={filter.options}
              contentClassName="max-h-[18.75rem] overflow-y-auto"
            />
          </div>
        ))}
      </div>
      <div role="group" aria-label="Options" className={cn('rounded-md border border-border divide-y overflow-y-auto', maxHeightClassName)}>
        {visible.length === 0 ? (
          <div className="px-3 py-2 text-sm text-gray-500">{emptyText}</div>
        ) : (
          visible.map((option) => (
            <label key={option.value} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50">
              <Checkbox checked={selected.has(option.value)} disabled={disabled} onCheckedChange={(checked) => toggle(option.value, checked)} aria-label={option.label} />
              <span className="min-w-0 truncate">{option.label}</span>
              {option.description ? <span className="text-xs text-gray-500 truncate">{option.description}</span> : null}
            </label>
          ))
        )}
      </div>
      <div className="text-xs text-gray-500">{value.length} selected</div>
    </div>
  );
}
